import { RegionShared } from "@1games-ru/region-shared";
import { isEmpty } from "lodash";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, SoundButton } from "@/shared/ui";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import VehicleCardButton from "@/shared/widgets/VehicleCardButton";
import useClearLot from "@/domains/transportWarehouse/hooks/useClearLot";
import useTransportWarehouseCacheData from "@/domains/transportWarehouse/hooks/useTransportWarehouseCacheData";
import TransportWarehouseTransportPinTransportModalContainer from "@/domains/transportWarehouse/containers/TransportWarehouseTransportPinTransportModalContainer";
import TransportWarehouseVehicleDetailsContainer from "@/domains/transportWarehouse/containers/TransportWarehouseVehicleDetailsContainer";
import TransportWarehouseTransportAddButton from "@/domains/transportWarehouse/components/transport/TransportWarehouseTransportAddButton";
import TransportWarehouseTransportUnlockSlotButton from "@/domains/transportWarehouse/components/transport/TransportWarehouseTransportUnlockSlotButton";
import TransportWarehouseTransportUnlockSlotModal from "@/domains/transportWarehouse/components/transport/TransportWarehouseTransportUnlockSlotModal";

type Props = {
  isFirstRender?: boolean;
};

export default function TransportWarehouseTransportContainer({ isFirstRender }: Props) {
  const { t } = useTranslation("transportWarehouse");
  const [vehicleDetails, setVehicleDetails] = useState<
    typeof RegionShared.Backend.Requests.GarageGetVehicleInfoRequest | null
  >(null);
  const [isOpenUnlockModal, setIsOpenUnlockModal] = useState(false);
  const [slotIndex, setSlotIndex] = useState(-1);
  const { clearLot, isClearingLot } = useClearLot();
  const { transportWarehouseInfo } = useTransportWarehouseCacheData();

  const reset = useCallback(() => {
    setSlotIndex(-1);
    setVehicleDetails(null);
  }, []);

  const header = isEmpty(vehicleDetails) ? (
    "Транспорт на складе"
  ) : (
    <div className="flex items-center gap-2.5">
      <Button
        variant="soft"
        color="gray"
        className="p-0 w-[2rem] h-[2rem] shrink-0 font-arial text-[0.7rem]"
        onClick={reset}
      >
        &#9668;
      </Button>
      <div className="text-18 text-white font-bold">{t("transportWarehouse.transportInfo")}</div>
    </div>
  );

  const footer = isEmpty(vehicleDetails) ? null : (
    <SoundButton
      className="w-full h-11"
      onClick={() => {
        if (transportWarehouseInfo) {
          clearLot({
            garageId: transportWarehouseInfo.id,
            index: slotIndex,
          });
          reset();
        }
      }}
      loading={isClearingLot}
      disabled={isClearingLot}
    >
      {t("transportWarehouse.transportUnpin")}
    </SoundButton>
  );

  return (
    <DeviceTabContentWrapper
      header={header}
      isStaticFirst={isFirstRender}
      className="p-0"
      footer={footer}
      onClose={reset}
    >
      {isEmpty(vehicleDetails) ? (
        <div className="grid grid-cols-3 gap-3.5">
          {transportWarehouseInfo.vehicles.map((item, index) =>
            item ? (
              <VehicleCardButton
                key={item.id}
                full
                model={item.model}
                brand={item.brand}
                plateNumber={item.plateNumber}
                fuel={item.fuel}
                status={item.status}
                className="h-[15.75rem]"
                onClick={() => {
                  setVehicleDetails({ garageId: transportWarehouseInfo.id, vehicleId: item.id });
                  setSlotIndex(index);
                }}
              />
            ) : (
              <TransportWarehouseTransportAddButton
                key={`${-index}`}
                onClick={() => setSlotIndex(index)}
                className="h-[15.75rem]"
              />
            ),
          )}

          <TransportWarehouseTransportUnlockSlotButton onClick={() => setIsOpenUnlockModal(true)} />
        </div>
      ) : (
        <TransportWarehouseVehicleDetailsContainer {...vehicleDetails} />
      )}

      {isOpenUnlockModal && (
        <TransportWarehouseTransportUnlockSlotModal
          price={200}
          onClose={() => setIsOpenUnlockModal(false)}
          onConfirm={() => setIsOpenUnlockModal(false)}
          isConfirming={false}
        />
      )}
      {slotIndex > -1 && !vehicleDetails && (
        <TransportWarehouseTransportPinTransportModalContainer
          slotIndex={slotIndex}
          onClose={() => setSlotIndex(-1)}
        />
      )}
    </DeviceTabContentWrapper>
  );
}
